import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HelpCircle,
  Mail,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Luggage,
  Map,
  CloudSun,
  Share2,
  Sparkles,
  LayoutTemplate
} from 'lucide-react';
import LegalLayout from '../LegalLayout';

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  const accentColor = '#f59e0b';

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)'
      }}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left transition-colors"
        style={{ color: 'var(--text-primary)' }}
      >
        <span className="font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: accentColor }} />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
        )}
      </button>
      {isOpen && (
        <div
          className="px-6 pb-4"
          style={{ color: 'var(--text-secondary)' }}
        >
          {answer}
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const accentColor = '#f59e0b';
  const [openFAQ, setOpenFAQ] = useState(0);

  const faqs = [
    {
      icon: <Luggage className="w-5 h-5" />,
      question: "How do I create a new trip?",
      answer: (
        <div className="space-y-2">
          <p>Creating a trip in PackPal is easy:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Tap the <strong>Create Trip</strong> button on the Home screen</li>
            <li>Enter your <strong>destination</strong></li>
            <li>Set your <strong>travel dates</strong> (start and end)</li>
            <li>Select <strong>activities</strong> you plan to do (sightseeing, hiking, business, etc.)</li>
            <li>PackPal will fetch weather data and generate smart packing recommendations</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Tip: The more activities you select, the more tailored your packing recommendations will be.
          </p>
        </div>
      )
    },
    {
      icon: <LayoutTemplate className="w-5 h-5" />,
      question: "How do I use packing templates?",
      answer: (
        <div className="space-y-2">
          <p>PackPal includes 7 pre-built templates for common trip types:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Business Trip</strong> — Professional essentials, work documents, formal attire</li>
            <li><strong>Weekend Getaway</strong> — Light packing for short trips</li>
            <li><strong>Beach Vacation</strong> — Swimwear, sunscreen, beach essentials</li>
            <li><strong>Hiking Adventure</strong> — Outdoor gear, trail supplies, layers</li>
            <li><strong>City Break</strong> — Walking shoes, camera, casual outfits</li>
            <li><strong>Ski Trip</strong> — Winter gear, thermal layers, ski equipment</li>
            <li><strong>Camping</strong> — Tent, sleeping bag, outdoor cooking gear</li>
          </ul>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Browse templates from the Templates tab and apply them to any trip.
          </p>
        </div>
      )
    },
    {
      icon: <Sparkles className="w-5 h-5" />,
      question: "How do AI recommendations work?",
      answer: (
        <div className="space-y-2">
          <p>PackPal's AI considers multiple factors to suggest items:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Destination:</strong> Location-specific items and cultural considerations</li>
            <li><strong>Weather:</strong> Real-time weather data for your travel dates</li>
            <li><strong>Activities:</strong> Gear and supplies for your planned activities</li>
            <li><strong>Trip Duration:</strong> Quantity recommendations based on how long you'll be away</li>
          </ul>
          <p className="mt-3">Recommendations are organized into 15 categories with priority levels (Essential, High, Medium, Low) so you know what to pack first.</p>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            AI suggestions are a helpful starting point — always review and customize based on your personal needs.
          </p>
        </div>
      )
    },
    {
      icon: <CloudSun className="w-5 h-5" />,
      question: "How does weather integration work?",
      answer: (
        <div className="space-y-2">
          <p>PackPal uses Apple WeatherKit to provide weather data:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>When you create a trip, PackPal fetches the weather forecast for your destination</li>
            <li>Weather data includes temperature, conditions, and precipitation</li>
            <li>This information is used to adjust packing recommendations (e.g., rain gear, warm layers)</li>
            <li>A visual weather preview is shown on your trip details</li>
          </ul>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Weather data requires iOS 16 or later. If weather is unavailable, PackPal uses intelligent fallback data.
          </p>
        </div>
      )
    },
    {
      icon: <Map className="w-5 h-5" />,
      question: "How do I generate a trip itinerary?",
      answer: (
        <div className="space-y-2">
          <p>PackPal can create a day-by-day itinerary for your trip:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open an existing trip from the <strong>Trips</strong> tab</li>
            <li>Tap the <strong>Itinerary</strong> section</li>
            <li>PackPal will generate a suggested daily plan based on your destination and activities</li>
            <li>Review and customize the itinerary to your preferences</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Itineraries are AI-generated suggestions — feel free to adjust them to match your travel style.
          </p>
        </div>
      )
    },
    {
      icon: <Share2 className="w-5 h-5" />,
      question: "How do I share my packing list?",
      answer: (
        <div className="space-y-2">
          <p>You can share your packing list with travel companions:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open the trip whose packing list you want to share</li>
            <li>Tap the <strong>Share</strong> button</li>
            <li>PackPal formats your list as rich text with categories and check marks</li>
            <li>Choose your sharing method (AirDrop, Messages, Email, etc.)</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            The shared list includes item names, categories, and packed status in a clean, readable format.
          </p>
        </div>
      )
    }
  ];

  return (
    <LegalLayout app="PackPal" docType="Support" icon={HelpCircle}>
        {/* Contact Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <a
            href="mailto:support@feraltechlabs.co.in"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <Mail className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Email Support
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  support@feraltechlabs.co.in
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              We typically respond within 24-48 hours
            </p>
          </a>

          <a
            href="mailto:support@feraltechlabs.co.in?subject=PackPal%20Bug%20Report"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <MessageSquare className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Report a Bug
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Help us improve PackPal
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Include your iOS version and steps to reproduce
            </p>
          </a>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>

        {/* App Info */}
        <div
          className="rounded-xl p-6"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            App Information
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>App Name:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>PackPal</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Developer:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Feral Techlab</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Platform:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>iOS 16+</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Category:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Travel</span>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link
            to="/apps/packpal/privacy"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Privacy Policy
          </Link>
          <span style={{ color: 'var(--text-tertiary)' }}>•</span>
          <Link
            to="/apps/packpal/terms"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Terms & Conditions
          </Link>
        </div>
    </LegalLayout>
  );
};

export default Support;
